import { ShieldCheckIcon, ShieldIcon } from "@strust/ui";
import { css } from "@strust/ui/css";
import type { ReactNode } from "react";
import { defineMessages, useIntl } from "react-intl";
import { CurrentInstallationBadge } from "../../../shared/installation-gate.tsx";
import { shellMessages } from "../../shell/messages.ts";

/** Below 1100 px the hero is hidden and the card stands alone (design D3, D4). */
const SPLIT = "@media (min-width: 1100px)";

const PAGE = css({
  minHeight: "100vh",
  display: "grid",
  gridTemplateColumns: "1fr",
  backgroundColor: "bg",
  color: "text",
  [SPLIT]: { gridTemplateColumns: "minmax(0, 61fr) minmax(0, 39fr)" },
});
const BRAND_PANEL = css({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  px: "6",
  py: "6",
  backgroundImage:
    "radial-gradient(60% 50% at 30% 40%, {colors.accent.soft}, transparent), linear-gradient(180deg, {colors.surface.2}, {colors.bg})",
  [SPLIT]: {
    px: "14",
    py: "14",
    borderRightWidth: "1px",
    borderRightStyle: "solid",
    borderRightColor: "line",
  },
});
// The brand and, at the other end, the installation's environment (spec explorer-access, "[FR]
// Installation environment badge": the login and invitation pages show it too).
const BRAND_ROW = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: "3",
});
const BRAND = css({ display: "flex", alignItems: "center", gap: "3" });
const MARK = css({
  display: "inline-grid",
  placeItems: "center",
  flexShrink: "0",
  width: "10",
  height: "10",
  borderRadius: "md",
  backgroundImage: "linear-gradient(135deg, {colors.accent}, {colors.accent.2})",
  boxShadow: "0 0 16px {colors.accent.line}",
  color: "on.accent",
  "& svg": { width: "icon.xl", height: "icon.xl" },
});
const WORDMARK = css({ display: "flex", flexDirection: "column" });
const NAME = css({ textStyle: "title", color: "text" });
const PRODUCT = css({ textStyle: "overline", color: "text.2" });
const HERO = css({
  display: "none",
  [SPLIT]: {
    display: "flex",
    flexDirection: "column",
    gap: "5",
    maxWidth: "35rem",
    mt: "auto",
    mb: "auto",
  },
});
const EYEBROW = css({ textStyle: "overline", color: "accent", m: "0" });
const HEADLINE = css({
  textStyle: "display",
  fontSize: "6xl",
  lineHeight: "tight",
  color: "text",
  m: "0",
});
const LEAD = css({
  textStyle: "body",
  fontSize: "xl",
  color: "text.2",
  m: "0",
  lineHeight: "normal",
});
const PROOF = css({ display: "none", m: "0", [SPLIT]: { display: "flex", gap: "8" } });
const PROOF_ITEM = css({ display: "flex", flexDirection: "column", gap: "1", m: "0" });
const PROOF_VALUE = css({
  textStyle: "heading",
  fontSize: "3xl",
  fontFamily: "display",
  color: "text",
  m: "0",
  order: "-1",
});
const PROOF_LABEL = css({ textStyle: "monoSm", color: "text.2", m: "0" });

const brandMessages = defineMessages({
  eyebrow: { id: "access.brand.eyebrow", defaultMessage: "Zero trust · Geolocation · Risk" },
  headline: {
    id: "access.brand.headline",
    defaultMessage: "Every signal verified. Every decision explained.",
  },
  lead: {
    id: "access.brand.lead",
    defaultMessage:
      "Investigate players, devices and locations across your operators, with audited access to precise data.",
  },
  proofBlockValue: { id: "access.brand.proof.block.value", defaultMessage: "immediate" },
  proofBlockLabel: { id: "access.brand.proof.block.label", defaultMessage: "block propagation" },
  proofWindowValue: { id: "access.brand.proof.window.value", defaultMessage: "≤1800 s" },
  proofWindowLabel: {
    id: "access.brand.proof.window.label",
    defaultMessage: "revalidation window",
  },
  proofAuditValue: { id: "access.brand.proof.audit.value", defaultMessage: "100%" },
  proofAuditLabel: {
    id: "access.brand.proof.audit.label",
    defaultMessage: "sensitive reads audited",
  },
});

const MAIN = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  px: "4",
  py: "8",
  minWidth: "0",
});
const CARD = css({
  layerStyle: "panel",
  width: "full",
  maxWidth: "25rem",
  display: "flex",
  flexDirection: "column",
  gap: "4",
  p: "8",
  boxShadow: "0 24px 64px {colors.shadow}",
});

/**
 * Full-viewport frame of the public screens (login, invitation), rendered outside the app shell:
 * the brand panel on the left (large screens) and the card on the right. The card is the page's
 * `main` landmark; the brand block is decorative.
 */
export function AuthLayout({
  children,
  busy,
}: {
  readonly children: ReactNode;
  readonly busy?: boolean;
}) {
  const intl = useIntl();
  return (
    <div className={PAGE}>
      <div className={BRAND_PANEL}>
        <div className={BRAND_ROW}>
          <div className={BRAND}>
            <span aria-hidden="true" className={MARK}>
              <ShieldCheckIcon />
            </span>
            <span className={WORDMARK}>
              <span className={NAME}>{intl.formatMessage(shellMessages.logo)}</span>
              <span className={PRODUCT}>{intl.formatMessage(shellMessages.productName)}</span>
            </span>
          </div>
          <CurrentInstallationBadge />
        </div>
        <div className={HERO}>
          <p className={EYEBROW}>{intl.formatMessage(brandMessages.eyebrow)}</p>
          <p className={HEADLINE}>{intl.formatMessage(brandMessages.headline)}</p>
          <p className={LEAD}>{intl.formatMessage(brandMessages.lead)}</p>
        </div>
        <dl className={PROOF}>
          {(
            [
              [brandMessages.proofBlockValue, brandMessages.proofBlockLabel],
              [brandMessages.proofWindowValue, brandMessages.proofWindowLabel],
              [brandMessages.proofAuditValue, brandMessages.proofAuditLabel],
            ] as const
          ).map(([value, label]) => (
            <div key={value.id} className={PROOF_ITEM}>
              <dt className={PROOF_LABEL}>{intl.formatMessage(label)}</dt>
              <dd className={PROOF_VALUE}>{intl.formatMessage(value)}</dd>
            </div>
          ))}
        </dl>
      </div>
      <main id="main-content" className={MAIN} aria-busy={busy ? true : undefined}>
        <div className={CARD}>{children}</div>
      </main>
    </div>
  );
}

const TITLE = css({ textStyle: "title", fontFamily: "display", color: "text", m: "0" });
const SUBTITLE = css({ textStyle: "bodySm", color: "text.2", m: "0" });
const HEAD = css({ display: "flex", flexDirection: "column", gap: "1.5" });

/** Card heading of a public screen: the page's only `h1`, an optional badge above and a subtitle. */
export function AuthHeading({
  title,
  subtitle,
  badge,
}: {
  readonly title: string;
  readonly subtitle?: ReactNode;
  readonly badge?: ReactNode;
}) {
  return (
    <div className={HEAD}>
      {badge}
      <h1 className={TITLE}>{title}</h1>
      {subtitle ? <p className={SUBTITLE}>{subtitle}</p> : null}
    </div>
  );
}

const NOTE = css({
  display: "flex",
  alignItems: "flex-start",
  gap: "2",
  m: "0",
  textStyle: "caption",
  color: "text.2",
  "& svg": { width: "icon.md", height: "icon.md", flexShrink: "0", mt: "0.5" },
});

/** Footer note of a public card: a shield glyph and a short sentence about audited, single-use access. */
export function AuthNote({ children }: { readonly children: ReactNode }) {
  return (
    <p className={NOTE}>
      <ShieldIcon aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}
